<template>
  <div class="min-h-screen flex flex-col items-center py-10 px-4">
    <!-- Header -->
     <div class="mobile-padding"></div>
    <div class="liquidGlassCard rounded-3xl p-6 w-full relative pt-14">
      <button class="absolute w-8 h-8 left-5 top-5 " @click="$router.back">
        <ColorImg
          src="/images/icons/back.png"
          class="bg-current w-full h-full"
          alt=""
        />
      </button>
      <h1 class="text-2xl font-semibold text-gray-800 text-center">
        Vérification KYC de Rider
      </h1>
      <p class="text-gray-500 text-center mt-2">
        Veuillez remplir soigneusement vos informations pour finaliser votre
        enregistrement.
      </p>
    </div>
    <!-- KYC Form -->
    <div class="liquidGlassCard rounded-3xl p-6 w-full mt-6">
      <FormComponent :fields="kycFields" :onFormSubmited="handleKycSubmit" :loading="isSaving">
        <template #submitButtonText>
          <div class="w-full">{{ isSaving ? 'Envoi en cours...' : 'Envoyer mon KYC' }}</div>
        </template>
      </FormComponent>
    </div>
  </div>
</template>

<script setup>
import Events from '@/events/events'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import FormComponent from '@/presentation/components/input/FormComponent.vue'
import { EVENTS } from '@/shared/constants/commons'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { saveRiderKyc } from '@/project/data/services/saveRiderKyc.js'

const router = useRouter()
const isSaving = ref(false)

const kycFields = [
  // === INFORMATIONS PERSONNELLES ===
  {
    label: 'Nom complet',
    propertyName: 'fullName',
    type: 'text',
    required: true,
    placeHolder: 'Entrez votre nom complet',
  },
  {
    label: 'Adresse e-mail',
    propertyName: 'email',
    type: 'email',
    required: true,
    placeHolder: 'Ex: rider@example.com',
  },
  {
    label: 'Date de naissance',
    propertyName: 'birthDate',
    type: 'date',
    required: true,
  },
  {
    label: 'Sexe',
    propertyName: 'gender',
    type: 'select',
    options: ['Homme', 'Femme', 'Autre'],
    required: true,
  },
  {
    label: 'Nationalité',
    propertyName: 'nationality',
    type: 'text',
    required: true,
  },
  {
    label: 'Numéro de téléphone',
    propertyName: 'phoneNumber',
    type: 'text',
    required: true,
    placeHolder: '+229 67 00 00 00',
    validate: v =>
      /^(\+?\d{8,15})$/.test(v) ? '' : 'Numéro de téléphone invalide',
  },

  // === ADRESSE ===
  {
    label: 'Adresse complète',
    propertyName: 'address',
    type: 'textarea',
    required: true,
    placeHolder: 'Rue, quartier, ville',
  },
  {
    label: 'Pays',
    propertyName: 'country',
    type: 'text',
    required: true,
  },
  {
    label: 'Ville / Commune',
    propertyName: 'city',
    type: 'text',
    required: true,
  },

  // === DOCUMENT D'IDENTITÉ ===
  {
    label: "Type de pièce d'identité",
    propertyName: 'idType',
    type: 'select',
    options: [
      "Carte nationale d'identité",
      'Passeport',
      'Permis de conduire',
      'Autre',
    ],
    required: true,
  },
  {
    label: 'Numéro de la pièce',
    propertyName: 'idNumber',
    type: 'text',
    required: true,
  },
  {
    label: "Date d'expiration de la pièce",
    propertyName: 'idExpiryDate',
    type: 'date',
  },

  // === PERMIS ET VÉHICULE ===
  {
    label: 'Type de véhicule utilisé',
    propertyName: 'vehicleType',
    type: 'select',
    options: ['Moto', 'Voiture', 'Vélo', 'Autre'],
    required: true,
  },
  {
    label: 'Numéro du permis de conduire',
    propertyName: 'licenseNumber',
    type: 'text',
    required: f => f.vehicleType === 'Voiture',
    showFieldIf: f => f.vehicleType === 'Voiture',
  },
  {
    label: "Date d'expiration du permis",
    propertyName: 'licenseExpiry',
    type: 'date',
    showFieldIf: f => f.vehicleType === 'Voiture',
  },
  {
    label: 'Immatriculation du véhicule',
    propertyName: 'vehicleRegistration',
    type: 'text',
    showFieldIf: f => f.vehicleType !== 'Vélo',
  },

  // === AUTRES ===
  {
    label: "Numéro d'urgence",
    propertyName: 'emergencyContact',
    type: 'text',
    placeHolder: '+229 90 00 00 00',
  },
  {
    label: 'Numéro Mobile Money',
    propertyName: 'bankAccount',
    type: 'text',
  },
]

async function handleKycSubmit(formData) {
  console.log('KYC Form submitted:', formData)

  // Validate required fields
  const missingFields = kycFields
    .filter(field => {
      const isVisible = field.showFieldIf ? field.showFieldIf(formData) : true
      if (!isVisible) return false
      const isRequired = typeof field.required === 'function' ? field.required(formData) : field.required
      return isRequired && !formData[field.propertyName]
    })
    .map(field => field.label)

  if (missingFields.length > 0) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Champs manquants',
      body: `Veuillez remplir : ${missingFields.join(', ')}`,
      type: 'error',
    })
    return
  }

  isSaving.value = true
  try {
    const result = await saveRiderKyc(formData)
    if (result.success) {
      onKycSaved(result)
    } else {
      onKycSaveFailed(result)
    }
  } catch (err) {
    console.error('handleKycSubmit unexpected error:', err)
    onKycSaveFailed({ message: err.message })
  } finally {
    isSaving.value = false
  }
}

function onKycSaved(result) {
  console.log('KYC saved successfully:', result)

  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: result.message || 'Votre KYC a été envoyé avec succès. Il sera validé sous peu.',
    type: 'success',
  })

  setTimeout(() => {
    router.push('/profile')
  }, 2000)
}

function onKycSaveFailed(result) {
  console.error('KYC save failed:', result)

  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur de sauvegarde',
    body: result?.message || "Une erreur s'est produite lors de la sauvegarde. Veuillez réessayer.",
    type: 'error',
  })
}
</script>

<style scoped>
button {
  transition: all 0.25s ease;
}
</style>
